// Configuration utilities for Clinical Scheduling Dashboard V3
// Environment-based configuration management

import type { AppConfig, DelRichtSite } from '@/types';

// =============================================================================
// Environment Variable Parsing
// =============================================================================
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] || defaultValue;
};

const getEnvNumber = (key: string, defaultValue: number): number => {
  const value = import.meta.env[key];
  return value ? parseInt(value, 10) : defaultValue;
};

const getEnvBoolean = (key: string, defaultValue: boolean): boolean => {
  const value = import.meta.env[key];
  return value ? value.toLowerCase() === 'true' : defaultValue;
};

// =============================================================================
// Application Configuration
// =============================================================================
export const appConfig: AppConfig = {
  environment: getEnvVar('VITE_ENVIRONMENT', 'production') as 'development' | 'production',
  crioApiEndpoint: getEnvVar(
    'VITE_CRIO_API_ENDPOINT',
    'https://flash-crio-proxy-480267397633.us-central1.run.app'
  ),
  geminiBotApi: getEnvVar(
    'VITE_GEMINI_CHATBOT_API',
    'https://flash-chatbot-480267397633.us-central1.run.app'
  ),
  flashBotApi: getEnvVar(
    'VITE_FLASH_CHATBOT_API',
    'https://flash-chatbot-480267397633.us-central1.run.app'
  ),
  // The shared capacity-declaration calendar, NOT a personal one — see
  // src/config/capacityCalendar.ts. The env var keeps its legacy name because it
  // is baked at build time through a Dockerfile ARG and deploy.sh; the new name
  // is preferred when present so the pipeline can migrate without a flag day.
  capacityCalendarUserId: getEnvNumber(
    'VITE_CAPACITY_CALENDAR_ID',
    getEnvNumber('VITE_TYLER_COORDINATOR_ID', 5540),
  ),
  capacityCalendarEmail: getEnvVar(
    'VITE_CAPACITY_CALENDAR_EMAIL',
    getEnvVar('VITE_TYLER_COORDINATOR_EMAIL', 'thastings@delricht.com'),
  ),
  delrichtSitesCount: getEnvNumber('VITE_DELRICHT_SITES_COUNT', 43),
  googleSheetsEnabled: getEnvBoolean('VITE_GOOGLE_SHEETS_ENABLED', true),
  debugMode: getEnvBoolean('VITE_DEBUG_MODE', false),
  logLevel: getEnvVar('VITE_LOG_LEVEL', 'info') as 'debug' | 'info' | 'warn' | 'error',
  googleClientId: getEnvVar('VITE_GOOGLE_CLIENT_ID', ''),
  allowedGoogleDomain: getEnvVar('VITE_ALLOWED_GOOGLE_DOMAIN', 'delricht.com'),
};

// =============================================================================
// System Constants
// =============================================================================
export const SYSTEM_CONSTANTS = {

  // Time ranges for calendar display
  BUSINESS_HOURS: {
    START: '04:00',
    END: '18:00'
  },

  // Cache durations
  CACHE_DURATIONS: {
    GOOGLE_SHEETS: 60 * 60 * 1000, // 1 hour
    CRIO_AUTH: 8 * 60 * 60 * 1000, // 8 hours
    API_RESPONSE: 5 * 60 * 1000,   // 5 minutes
  },

  // Default site for initial load
  DEFAULT_SITE_ID: '2327', // ATL - General Medicine

  // Refresh intervals
  REFRESH_INTERVALS: {
    GOOGLE_SHEETS: 5 * 60 * 1000, // 5 minutes
    AVAILABILITY: 2 * 60 * 1000,  // 2 minutes
  },

  // Visit duration mappings (in minutes)
  VISIT_DURATIONS: {
    '1 - Recruitment': 30,
    '3 - Screening': 90,
    '5 - Baseline': 90,
    '7 - Maintenance': 60,
    '8 - Safety Call': 30,
  },
} as const;

// =============================================================================
// API Endpoints
// =============================================================================
export const apiEndpoints = {
  crio: {
    base: appConfig.crioApiEndpoint,
    sites: '/crio/production/sites',
    studies: '/crio/production/studies',
    availability: '/crio/production/calendar/availability',
    appointment: '/crio/production/appointment',
    health: '/health',
  },
  geminiBot: {
    base: appConfig.geminiBotApi,
    health: '/health',
    debug: '/debug',
    // Trial Management
    trials: '/api/dashboard/trials',
    trialById: (id: number) => `/api/dashboard/trials/${id}`,
    criteria: (trialId: number) => `/api/dashboard/trials/${trialId}/criteria`,
    // Conversations
    conversations: '/api/dashboard/conversations',
    conversationById: (sessionId: string) => `/api/dashboard/conversations/${sessionId}`,
  },
  flashBot: {
    base: appConfig.flashBotApi,
    health: '/health',
    debug: '/debug',
    // Chat endpoint (flash-specific)
    chat: '/api/flash/chat',
    // Protocols (served by flash-chatbot)
    // Analytics endpoints (PostgreSQL BI)
    analytics: {
      stats: '/api/dashboard/stats',
      statsDetailed: '/api/dashboard/stats/detailed',
      conversionFunnel: '/api/dashboard/analytics/bi/conversion-funnel',
      conditionPerformance: '/api/dashboard/analytics/bi/condition-performance',
      locationEffectiveness: '/api/dashboard/analytics/bi/location-effectiveness',
      questionImpact: '/api/dashboard/analytics/bi/question-impact',
      timePatterns: '/api/dashboard/analytics/bi/time-patterns',
      eligibilityOutcomes: '/api/dashboard/analytics/bi/eligibility-outcomes',
      highAbandonment: '/api/dashboard/analytics/bi/high-abandonment-trials',
      screenFailureFunnel: '/api/dashboard/analytics/bi/screen-failure-funnel',
      criterionFailures: '/api/dashboard/analytics/bi/criterion-failures',
      nearMiss: '/api/dashboard/analytics/bi/near-miss',
      smsCampaignRoi: '/api/dashboard/analytics/bi/sms-campaign-roi',
      summary: '/api/dashboard/analytics/bi/summary',
      searchGaps: '/api/dashboard/analytics/bi/search-gaps',
      searchDemand: '/api/dashboard/analytics/bi/search-demand',
      searchPerformance: '/api/dashboard/analytics/bi/search-performance',
      sessionQuality: '/api/dashboard/analytics/bi/session-quality',
      dropoffAnalysis: '/api/dashboard/analytics/bi/dropoff-analysis',
      questionEffectiveness: '/api/dashboard/analytics/bi/question-effectiveness',
    },
    systemHealth: {
      performance: '/api/dashboard/system-health/performance',
      cloudLogs: '/api/dashboard/system-health/cloud-logs',
    },
    bdProspecting: {
      search: '/api/dashboard/bd-prospecting/search',
      study: (nctId: string) => `/api/dashboard/bd-prospecting/study/${nctId}`,
      compare: '/api/dashboard/bd-prospecting/compare',
      feasibility: '/api/dashboard/bd-prospecting/feasibility',
      sponsorIntel: '/api/dashboard/bd-prospecting/sponsor-intel',
      sponsorNews: '/api/dashboard/bd-prospecting/sponsor-news',
      opportunities: '/api/dashboard/bd-prospecting/opportunities',
      zoominfoCompany: '/api/dashboard/bd-prospecting/zoominfo/company',
      zoominfoContacts: '/api/dashboard/bd-prospecting/zoominfo/contacts',
      zoominfoEnrich: '/api/dashboard/bd-prospecting/zoominfo/enrich',
      zoominfoSignals: '/api/dashboard/bd-prospecting/zoominfo/signals',
      zoominfoEnriched: '/api/dashboard/bd-prospecting/zoominfo/enriched',
    },
    selfReschedule: {
      createLink: '/api/reschedule/link',
      revoke: '/api/reschedule/token/revoke',
    },
    feasibility: {
      reengagement: '/api/dashboard/feasibility/reengagement',
    },
    analyticsPipelines: {
      embeddings: '/api/dashboard/analytics/pipelines/embeddings',
      sessionOutcomes: '/api/dashboard/analytics/pipelines/session-outcomes',
      turnScores: '/api/dashboard/analytics/pipelines/turn-scores',
      analyzers: '/api/dashboard/analytics/pipelines/analyzers',
      status: (jobId: string) => `/api/dashboard/analytics/pipelines/status/${jobId}`,
      statusAll: '/api/dashboard/analytics/pipelines/status',
    },
    protocolComparison: {
      compare: '/api/protocols/comparison/compare',
      similar: (trialId: number) => `/api/protocols/comparison/similar/${trialId}`,
      logsRecent: '/api/protocols/comparison/logs/recent',
      logDetail: (comparisonId: string) => `/api/protocols/comparison/logs/${comparisonId}`,
      feedbackCorrection: '/api/protocols/comparison/feedback/correction',
      feedbackStats: '/api/protocols/comparison/feedback/stats',
      embeddingsStatus: '/api/protocols/comparison/embeddings/status',
      embeddingsGenerateBatch: '/api/protocols/comparison/embeddings/generate-batch',
    },
  },
};

// =============================================================================
// Site Timezone Mapping
// =============================================================================
export const SITE_TIMEZONES: Record<string, string> = {
  // Eastern Time Zone Sites
  'ATL': 'America/New_York',   // Atlanta, GA
  'CHS': 'America/New_York',   // Charleston, SC
  'CLT': 'America/New_York',   // Charlotte, NC
  'CIN': 'America/New_York',   // Cincinnati, OH
  'IND': 'America/New_York',   // Indianapolis, IN
  'LOU': 'America/New_York',   // Louisville, KY
  'BET': 'America/New_York',   // Bethesda, MD (DC area)
  'STE': 'America/New_York',   // Sterling, VA

  // Central Time Zone Sites
  'NO': 'America/Chicago',     // New Orleans, LA
  'BR': 'America/Chicago',     // Baton Rouge, LA
  'NS': 'America/Chicago',     // Louisiana (North Shore)
  'DAL': 'America/Chicago',    // Dallas, TX
  'GU': 'America/Chicago',     // Gulfport, MS
  'HMA': 'America/Chicago',    // Hattiesburg, MS
  'NAS': 'America/Chicago',    // Nashville, TN
  'SPR': 'America/Chicago',    // Springfield, MO
  'STL': 'America/Chicago',    // St. Louis, MO
  'TUL': 'America/Chicago',    // Tulsa, OK
  'OVP': 'America/Chicago',    // Overland Park, KS (Kansas City area)

  // Mountain Time Zone Sites
  'SLC': 'America/Denver',     // Salt Lake City, UT

  // Master Site (use Central as default)
  'Master': 'America/Chicago',
};

// Helper function to get timezone for a site
export const getSiteTimezone = (siteName: string): string => {
  // Extract the site code from the name (e.g., "ATL - General Medicine" -> "ATL")
  const siteCode = siteName.split(' - ')[0].trim();
  return SITE_TIMEZONES[siteCode] || 'America/Chicago'; // Default to Central
};

// Helper function to adjust time for site timezone
// Assumes the source time is in Central Time and adjusts for the site's timezone
export const adjustTimeForSiteTimezone = (dateTime: Date, siteName: string): Date => {
  const siteTimezone = getSiteTimezone(siteName);

  // For Eastern sites, add 1 hour to Central Time
  if (siteTimezone === 'America/New_York') {
    const adjusted = new Date(dateTime);
    adjusted.setHours(adjusted.getHours() + 1);
    return adjusted;
  }

  // For Mountain sites, subtract 1 hour from Central Time
  if (siteTimezone === 'America/Denver') {
    const adjusted = new Date(dateTime);
    adjusted.setHours(adjusted.getHours() - 1);
    return adjusted;
  }

  // Central Time sites - no adjustment needed
  return dateTime;
};

// =============================================================================
// DelRicht Sites Configuration
// =============================================================================
export const delrichtSites: DelRichtSite[] = [
  // Core production sites - verified from CLAUDE.md
  { siteId: '2327', name: 'ATL - General Medicine', activeStudies: 38, enrollingStudies: 27 },
  { siteId: '3863', name: 'ATL - Psych', activeStudies: 5, enrollingStudies: 5 },
  { siteId: '2054', name: 'ATL - Dermatology', activeStudies: 7, enrollingStudies: 2 },
  { siteId: '1262', name: 'NO - Dermatology', activeStudies: 37, enrollingStudies: 17 },
  { siteId: '5383', name: 'BET - Psych', activeStudies: 2, enrollingStudies: 2 },
  { siteId: '1266', name: 'BR - Dermatology', activeStudies: 41, enrollingStudies: 23 },
  { siteId: '3957', name: 'BR - Psych', activeStudies: 4, enrollingStudies: 4 },
  { siteId: '1265', name: 'BR - General Medicine', activeStudies: 26, enrollingStudies: 5 },
  { siteId: '1316', name: 'BR - Neuro Medical', activeStudies: 10, enrollingStudies: 5 },
  { siteId: '4556', name: 'CHS - Dermatology', activeStudies: 6, enrollingStudies: 5 },
  { siteId: '2693', name: 'CHS - General Medicine', activeStudies: 19, enrollingStudies: 10 },
  { siteId: '4886', name: 'CIN - General Medicine', activeStudies: 4, enrollingStudies: 4 },
  { siteId: '3466', name: 'CLT - General Medicine', activeStudies: 15, enrollingStudies: 2 },
  { siteId: '5384', name: 'CLT - Psych', activeStudies: 1, enrollingStudies: 1 },
  { siteId: '2373', name: 'DAL - Dermatology', activeStudies: 11, enrollingStudies: 9 },
  { siteId: '4913', name: 'DAL - Psych', activeStudies: 2, enrollingStudies: 2 },
  { siteId: '1867', name: 'DAL - General Medicine', activeStudies: 17, enrollingStudies: 5 },
  { siteId: '1263', name: 'NO - Urology', activeStudies: 4, enrollingStudies: 0 },
  { siteId: '2306', name: 'GU - General Medicine (Kerby)', activeStudies: 22, enrollingStudies: 6 },
  { siteId: '1853', name: 'GU - General Medicine (Tamboli)', activeStudies: 1, enrollingStudies: 0 },
  { siteId: '1884', name: 'HMA - Dermatology', activeStudies: 1, enrollingStudies: 1 },
  { siteId: '4957', name: 'IND - General Medicine', activeStudies: 3, enrollingStudies: 3 },
  { siteId: '3500', name: 'LOU - General Medicine', activeStudies: 10, enrollingStudies: 5 },
  { siteId: '1818', name: 'Master Site', activeStudies: 2706, enrollingStudies: 2578 },
  { siteId: '2842', name: 'BET - General Medicine', activeStudies: 16, enrollingStudies: 10 },
  { siteId: '2881', name: 'NAS - Dermatology', activeStudies: 10, enrollingStudies: 7 },
  { siteId: '2518', name: 'NAS - General Medicine', activeStudies: 18, enrollingStudies: 7 },
  { siteId: '1261', name: 'NO - General Medicine', activeStudies: 34, enrollingStudies: 27 },
  { siteId: '5088', name: 'NO - Ophthalmology', activeStudies: 3, enrollingStudies: 3 },
  { siteId: '3834', name: 'NO - Psych/Rheum', activeStudies: 18, enrollingStudies: 13 },
  { siteId: '1642', name: 'NS - Dermatology', activeStudies: 1, enrollingStudies: 0 },
  { siteId: '2181', name: 'NS - General Medicine', activeStudies: 5, enrollingStudies: 4 },
  { siteId: '2372', name: 'NS - Neurology', activeStudies: 2, enrollingStudies: 1 },
  { siteId: '3468', name: 'OVP - General Medicine', activeStudies: 8, enrollingStudies: 8 },
  { siteId: '5282', name: 'SLC - Gen Med', activeStudies: 0, enrollingStudies: 0 },
  { siteId: '2517', name: 'SPR - General Medicine', activeStudies: 21, enrollingStudies: 12 },
  { siteId: '2071', name: 'STL - Dermatology', activeStudies: 3, enrollingStudies: 2 },
  { siteId: '3502', name: 'STE - General Medicine', activeStudies: 10, enrollingStudies: 6 },
  { siteId: '2840', name: 'STL - General Medicine', activeStudies: 15, enrollingStudies: 9 },
  { siteId: '1264', name: 'NO - Vax', activeStudies: 51, enrollingStudies: 23 },
  { siteId: '3941', name: 'TUL - Psych', activeStudies: 0, enrollingStudies: 0 },
  { siteId: '1305', name: 'TUL - General Medicine', activeStudies: 32, enrollingStudies: 13 },
  { siteId: '1409', name: 'TUL - Dermatology', activeStudies: 2, enrollingStudies: 1 },
];

// =============================================================================
// Validation Functions
// =============================================================================
export const validateConfig = (): boolean => {
  const requiredVars = [
    'VITE_CRIO_API_ENDPOINT',
    'VITE_TYLER_COORDINATOR_ID',
  ];

  const missing = requiredVars.filter(key => !import.meta.env[key]);

  if (missing.length > 0) {
    console.error('Missing required environment variables:', missing);
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
  }

  return true;
};

// =============================================================================
// Logging Configuration
// =============================================================================
export const createLogger = (component: string) => {
  const shouldLog = (level: string) => {
    const levels = { debug: 0, info: 1, warn: 2, error: 3 };
    const currentLevel = levels[appConfig.logLevel] || 1;
    const messageLevel = levels[level as keyof typeof levels] || 1;
    return messageLevel >= currentLevel;
  };

  return {
    debug: (message: string, ...args: any[]) => {
      if (shouldLog('debug')) {
        console.debug(`[${component}] ${message}`, ...args);
      }
    },
    info: (message: string, ...args: any[]) => {
      if (shouldLog('info')) {
        console.info(`[${component}] ${message}`, ...args);
      }
    },
    warn: (message: string, ...args: any[]) => {
      if (shouldLog('warn')) {
        console.warn(`[${component}] ${message}`, ...args);
      }
    },
    error: (message: string, ...args: any[]) => {
      if (shouldLog('error')) {
        console.error(`[${component}] ${message}`, ...args);
      }
    },
  };
};

// =============================================================================
// Export Everything
// =============================================================================
export default {
  appConfig,
  apiEndpoints,
  delrichtSites,
  validateConfig,
  createLogger,
};